import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { CompanyLogo } from "../../components/CompanyLogo";
import { Card, ErrorBox, FreshnessStamp, Skeleton } from "../../components/ui";
import { useLifecycleSummary } from "../../lib/api";
import { fmtNumber, fmtRelative, fmtValue, parseFrappeDatetime } from "../../lib/format";
import { SectionCard, Stat, pillClass, useDateFormat } from "./shared";

/** The lifecycle hub: who is here, who is joining or leaving, and what is waiting on somebody (WP-23.1).
 *
 * The headline numbers are counts the server already has, so the hub does not fetch the approval queue, the
 * attendance grid, the org chart or the payroll runs in order to say how many of each there are. */
export default function LifecycleHub({ company }: { company: string }) {
  const { t, i18n } = useTranslation();
  const lng = i18n.language;
  const q = useLifecycleSummary(company || undefined);
  const fmtDate = useDateFormat();

  if (q.isError) return <div className="mt-6"><ErrorBox error={q.error} onRetry={() => void q.refetch()} /></div>;
  if (!q.data) return <div className="mt-6 space-y-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-24" />)}</div>;
  const d = q.data;
  const waiting = d.waiting.leaves + d.waiting.expense_claims;

  return (
    <div className="mt-4 space-y-6" data-hr-lifecycle>
      <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-brand-grey">
        <span>{d.me ? t("hr.signedInAs", { name: d.me.employee_name, role: d.me.designation }) : t("hr.noEmployeeLink")}</span>
        <FreshnessStamp lastUpdated={d.as_of} />
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-6">
        <Stat label={t("hr.active")} value={d.employees.active} />
        <Stat label={t("hr.left")} value={d.employees.left} />
        <Stat label={t("hr.withManager")} value={`${d.employees.active ? Math.round((100 * d.employees.with_manager) / d.employees.active) : 0} %`} />
        <Stat label={t("hr.onLeaveToday")} value={d.on_leave_today} cls={d.on_leave_today ? "text-status-amber" : ""} />
        <Stat label={t("hr.pendingApprovals")} value={waiting} cls={waiting ? "text-status-red" : ""}
          hint={d.waiting.mine_only ? t("hr.approvalsMine") : undefined} />
        <Stat label={t("hr.openPositions")} value={d.open_positions} />
      </div>

      {waiting > 0 && (
        <Link to="/people/lifecycle/approvals" data-hr-waiting
          className="flex items-center justify-between gap-3 rounded-lg border border-status-red/40 bg-red-50 px-4 py-3 text-sm hover:border-status-red">
          <span className="font-semibold text-status-red">{t("hr.waitingOnYou", { count: waiting })}</span>
          <span className="text-xs font-semibold text-brand-primary">{t("hr.goToApprovals")} →</span>
        </Link>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {[{ key: "onboarding", rows: d.onboarding, title: t("hr.onboarding") },
          { key: "separations", rows: d.separations, title: t("hr.offboarding") }].map((sec) => (
          <Card key={sec.key} className="overflow-hidden" data-hr-boarding={sec.key}>
            <div className="border-b border-brand-grey-light px-4 py-3"><h2 className="font-heading text-base font-semibold">{sec.title}</h2></div>
            {sec.rows.length === 0 ? <div className="p-6 text-center text-sm text-brand-grey">–</div> : (
              <div className="divide-y divide-brand-grey-light">
                {sec.rows.map((r) => (
                  <div key={r.name} className="px-4 py-3 text-sm" data-hr-boarding-row={r.name}>
                    <div className="flex items-center justify-between gap-2">
                      <div>
                        <span className="font-semibold">{r.employee_name}</span>
                        <span className="text-xs text-brand-grey"> · {r.designation} · <CompanyLogo company={r.company} size={12} className="align-middle" /> {r.company}</span>
                      </div>
                      <span className={pillClass(r.boarding_status)}>{t(`hr.boarding.${r.boarding_status}`, r.boarding_status ?? "")}</span>
                    </div>
                    <div className="mt-1 text-[11px] text-brand-grey">
                      {"date_of_joining" in r
                        ? `${t("hr.joins")} ${fmtDate(r.date_of_joining)}`
                        : `${t("hr.resignation")} ${fmtDate((r as { resignation_letter_date: string }).resignation_letter_date)} · ${t("hr.lastDay")} ${fmtDate((r as { relieving_date: string }).relieving_date)}`}
                      {" · "}{r.tasks_done}/{r.tasks.length} {t("hr.tasksDone")}
                    </div>
                    <div className="mt-1 h-1.5 rounded bg-brand-grey-light">
                      <div className="h-1.5 rounded bg-brand-primary" style={{ width: `${r.tasks.length ? (100 * r.tasks_done) / r.tasks.length : 0}%` }} />
                    </div>
                    <ul className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-brand-grey">
                      {r.tasks.map((tk) => <li key={tk.subject}><span className={tk.status === "Completed" ? "text-status-green" : ""}>{tk.status === "Completed" ? "✓" : "○"}</span> {tk.subject}</li>)}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <SectionCard title={t("hr.timesheets")}
          aside={`${d.timesheets.count_180d} ${t("hr.sheets")} · ${fmtNumber(d.timesheets.hours_180d, lng, 0)} h · 6 ${t("hr.months")}`}>
          <div className="p-4 text-sm text-brand-grey">
            {t("hr.timesheetsHint")}
          </div>
        </SectionCard>

        <SectionCard title={t("hr.expenseClaims")}>
          <div className="grid grid-cols-3 gap-3 p-4 text-sm">
            {d.expense_claims.summary.map((s) => (
              <div key={s.approval_status ?? "?"} className="rounded-lg bg-[#F6F8FA] p-3">
                <div className="text-[11px] uppercase tracking-wide text-brand-grey">{t(`hr.claimStatus.${s.approval_status}`, s.approval_status ?? "")}</div>
                <div className="font-heading text-xl font-semibold">{s.n}</div>
                <div className="text-xs text-brand-grey">{fmtValue(s.amount, "CDF", lng)}</div>
              </div>
            ))}
          </div>
        </SectionCard>
      </div>

      <p className="text-[11px] text-brand-grey">
        {t("ppl.updated")} {fmtRelative(parseFrappeDatetime(d.as_of), lng)}
      </p>
    </div>
  );
}
